/**
 * RunAI OS Theme Context — İZOLE
 * Kendi localStorage key'i: 'os-theme'
 * Seçenekler: 'dark' | 'light' | 'system' (varsayılan)
 * 'system' → prefers-color-scheme dinler
 */
import { createContext, useContext, useEffect, useState } from 'react'

type OSTheme = 'dark' | 'light' | 'system'

interface OSThemeContextValue {
  theme: OSTheme
  effectiveTheme: 'dark' | 'light'
  toggle: () => void
  isDark: boolean
}

const OSThemeContext = createContext<OSThemeContextValue>({
  theme: 'system',
  effectiveTheme: 'dark',
  toggle: () => undefined,
  isDark: true,
})

export function OSThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<OSTheme>(() => {
    return (localStorage.getItem('os-theme') as OSTheme) ?? 'system'
  })

  const [systemPreference, setSystemPreference] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  // prefers-color-scheme değişikliğini dinle
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? 'dark' : 'light')
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Effective theme hesapla
  const effectiveTheme: 'dark' | 'light' = theme === 'system' ? systemPreference : theme

  // localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('os-theme', theme)
  }, [theme])

  // [data-area="os"] class'ını güncelle
  useEffect(() => {
    const osArea = document.querySelector('[data-area="os"]')
    if (osArea) {
      osArea.classList.remove('dark', 'light')
      osArea.classList.add(effectiveTheme)
    }
  }, [effectiveTheme])

  const toggle = () => {
    setTheme(prev => {
      if (prev === 'system') return 'dark'
      if (prev === 'dark') return 'light'
      return 'system'
    })
  }

  return (
    <OSThemeContext.Provider value={{ theme, effectiveTheme, toggle, isDark: effectiveTheme === 'dark' }}>
      {children}
    </OSThemeContext.Provider>
  )
}

export function useOSTheme() {
  return useContext(OSThemeContext)
}
